import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  Upload,
  message,
  Progress,
  Select,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { api, uploadDirectToBlob } from "../../utils/api";
import { AppTypes } from "../../enum/app";

const { TextArea } = Input;

const DEFAULT_VERSION = "1.0.0";
const AdminAppCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("");
  const [installFile, setInstallFile] = useState<File | null>(null);
  const [installFileUrl, setInstallFileUrl] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);
  const [installProgress, setInstallProgress] = useState(0);

  // Generate/revoke preview URL for selected logo
  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLogoPreviewUrl("");
    }
  }, [logoFile]);

  const isValid = useMemo(() => {
    const values = form.getFieldsValue();
    const hasBasics = values?.name && values?.description && values?.appInfo;
    const hasLogo = logoFile || logoUrl;
    const hasInstall = installFile || installFileUrl;
    return !!(hasBasics && hasLogo && hasInstall);
  }, [form, logoFile, logoUrl, installFile, installFileUrl]);

  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);
      // Derive a safe folder prefix from app name
      const slug = String(values.name || "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      const basePrefix = slug ? `admin-apps/${slug}` : "admin-apps/unknown-app";

      // Upload files on submit only
      let finalLogoUrl = logoUrl;
      let finalInstallUrl = installFileUrl;

      if (!finalLogoUrl && logoFile) {
        // upload logo under <prefix>/logo/
        setLogoProgress(0);
        const res = await uploadDirectToBlob(logoFile, `${basePrefix}/logo`, {
          onProgress: (ev) => {
            const pct = Math.round(
              (ev.loadedBytes / (logoFile.size || 1)) * 100,
            );
            setLogoProgress(pct);
          },
          blockSizeMB: 8,
          concurrency: 8,
        });
        finalLogoUrl = res.url;
        setLogoUrl(finalLogoUrl);
      }
      if (!finalInstallUrl && installFile) {
        // upload installer under <prefix>/installer/
        setInstallProgress(0);
        const res = await uploadDirectToBlob(
          installFile,
          `${basePrefix}/installer/${DEFAULT_VERSION}`,
          {
            onProgress: (ev) => {
              const pct = Math.round(
                (ev.loadedBytes / (installFile.size || 1)) * 100,
              );
              setInstallProgress(pct);
            },
            blockSizeMB: 8,
            concurrency: 8,
          },
        );
        finalInstallUrl = res.url;
        setInstallFileUrl(finalInstallUrl);
      }

      // Call backend create API with initial version 1.0.0
      await api.post("/api/admin/apps", {
        name: values.name,
        description: values.description,
        appInfo: values.appInfo,
        logoUrl: finalLogoUrl,
        type: values.type,
        initialVersion: {
          version: DEFAULT_VERSION,
          changes: "Initial release",
          installerUrl: finalInstallUrl,
        },
      });
      message.success("アプリを登録しました。");
      navigate("/admin");
    } catch (e) {
      message.error("アプリ登録に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = () => {
    setLogoFile(null);
    setLogoUrl("");
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div>
        <h2 className="text-lg font-normal mb-2 text-start">
          アプリの新規登録
        </h2>
        <Breadcrumb
          items={[
            { title: <Link to="/admin">アプリ一覧</Link> },
            { title: "アプリの新規登録" },
          ]}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        className="mt-2"
      >
        <div className="bg-[#F4F6F9] rounded-lg p-4">
          <div className="text-base font-normal mb-3 text-left">
            アプリの基本情報
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
            <Form.Item
              label={
                <span className="font-medium">
                  名前 <span className="text-red-500">※</span>
                </span>
              }
              name="name"
              rules={[{ required: true, message: "" }]}
            >
              <Input
                maxLength={255}
                className="base-input"
                placeholder="アプリ名を入力"
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={
                <span className="font-medium">
                  ロゴ <span className="text-red-500">※</span>
                </span>
              }
              required={false}
            >
              <div className="flex items-start gap-3 upload-container">
                <Upload
                  beforeUpload={(file: File) => {
                    setLogoFile(file);
                    return false;
                  }}
                  onRemove={handleRemove}
                  maxCount={1}
                  showUploadList={logoFile ? { showPreviewIcon: false } : false}
                  accept="image/*"
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="base-upload h-[46px] rounded-[6px] border px-[120px]"
                  >
                    アップロード
                  </Button>
                </Upload>
                {logoPreviewUrl && (
                  <img
                    src={logoPreviewUrl}
                    alt="Logo preview"
                    className="h-[46px] w-[46px] p-1 object-contain rounded border border-[#ebeef2] bg-white"
                  />
                )}
                {!logoPreviewUrl && logoUrl && (
                  <a
                    href={logoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#0077C8] text-sm"
                  >
                    プレビュー
                  </a>
                )}
              </div>
              {logoProgress > 0 && logoProgress < 100 && (
                <Progress
                  percent={logoProgress}
                  size="small"
                  className="w-full max-w-[378px]"
                />
              )}
            </Form.Item>

            <Form.Item
              label={
                <span className="font-medium">
                  説明 <span className="text-red-500">※</span>
                </span>
              }
              name="description"
              rules={[{ required: true, message: "" }]}
            >
              <Input
                maxLength={255}
                className="base-input"
                placeholder="アプリの説明を入力"
                allowClear
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="font-medium">
                  種類 <span className="text-red-500">※</span>
                </span>
              }
              name="type"
              rules={[{ required: true, message: "" }]}
            >
              <Select
                defaultValue={AppTypes.APP}
                className="base-select h-[46px] text-start"
                options={[
                  { value: AppTypes.APP, label: "アプリ" },
                  { value: AppTypes.PLUGIN, label: "プラグイン" },
                ]}
              />
            </Form.Item>
            <div className="col-span-2">
              <Form.Item
                label={
                  <span className="font-medium">
                    アプリ情報 <span className="text-red-500">※</span>
                  </span>
                }
                name="appInfo"
                rules={[{ required: true, message: "" }]}
              >
                <TextArea
                  className="base-input"
                  rows={6}
                  placeholder="アプリに関する詳細情報を入力"
                />
              </Form.Item>
            </div>
          </div>
        </div>

        <div className="bg-[#F4F6F9] rounded-lg p-4 mt-4">
          <Form.Item
            className="w-[calc(50%-12px)]"
            label={
              <span className="font-medium">
                インストールファイルのアップロード
                <span className="text-red-500">※</span>
              </span>
            }
          >
            <div className="flex flex-col gap-y-2 gap-x-6 items-start">
              <div className="flex items-start gap-3 upload-container w-full">
                <Upload
                  beforeUpload={(file: File) => {
                    setInstallFile(file);
                    return false;
                  }}
                  maxCount={1}
                  showUploadList={
                    installFile ? { showPreviewIcon: false } : false
                  }
                  accept=".exe,.msi,.zip,.rar,.7z,.dmg,.pkg"
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="base-upload w-full h-[46px] rounded-[6px] border px-[120px]"
                  >
                    アップロード
                  </Button>
                </Upload>
                {/* {installFile && (
                  <span className="text-sm text-gray-600 truncate max-w-[200px]">
                    {installFile.name}
                  </span>
                )} */}
                {installFileUrl && (
                  <a
                    href={installFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#0077C8] text-sm"
                  >
                    ファイルを確認
                  </a>
                )}
              </div>
              {installProgress > 0 && installProgress < 100 && (
                <Progress
                  percent={installProgress}
                  size="small"
                  className="w-full max-w-[378px]"
                />
              )}
            </div>
          </Form.Item>
        </div>

        <div className="flex justify-end mt-5">
          <Button
            htmlType="submit"
            loading={submitting}
            disabled={!isValid}
            className="base-button w-[120px] h-[46px]"
          >
            {submitting ? "登録中…" : "アプリ登録"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AdminAppCreatePage;
